// @ts-nocheck
/**
 * Licence Details modal — shows available vs. used counts for every licence
 * key on a user/company/call-centre. Ordered by KEY_ORDER first, then any
 * extra keys the backend sends. Ported to shadcn Dialog.
 */
import { RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LICENCE_LABEL } from '@/utilities/constant';

const KEY_ORDER = [
  'role_ccadmin',
  'role_ccagent',
  'role_teamlead',
  'live_agent',
  'channel',
  'cps',
  'mobile',
  'company',
  'demo_live_agents_per_reseller_company',
];

const labelFor = (key) =>
  LICENCE_LABEL[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function LicenceDetailsModal({ show, onHide, title, licence, loading, onRefresh }) {
  const available = licence?.available_licence || {};
  const used = licence?.used_licence || {};

  const allKeys = Array.from(new Set([...Object.keys(available), ...Object.keys(used)])).filter((k) => k !== 'team_lead');
  const orderedKeys = [
    ...KEY_ORDER.filter((k) => allKeys.includes(k)),
    ...allKeys.filter((k) => !KEY_ORDER.includes(k)),
  ];

  return (
    <Dialog open={show} onOpenChange={(next) => !next && onHide?.()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title || 'Licence Details'}</DialogTitle>
        </DialogHeader>

        <div className="mb-3 flex justify-end">
          <Button size="sm" onClick={onRefresh} disabled={loading} className="gap-1.5">
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">Loading licence…</div>
        ) : orderedKeys.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No licence data available.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Licence
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Available
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Used
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderedKeys.map((key) => (
                  <tr key={key} className="border-t border-border">
                    <td className="px-3 py-2">{labelFor(key)}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {Number(available[key] || 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {Number(used[key] || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
